import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { FeatureFlag } from '@/client/types/feature-flags';
import { useRevertFeatureFlagMutation } from '@/client/api/feature-flags/feature-flags.mutation';
import { featureFlagPermissionError } from './utils';

interface RevertToDefaultDialogProps {
  isOpen: boolean;
  onClose: () => void;
  flag: FeatureFlag | null;
}

const RevertToDefaultDialog: React.FC<RevertToDefaultDialogProps> = ({ isOpen, onClose, flag }) => {
  const revertMutation = useRevertFeatureFlagMutation();

  const handleRevert = () => {
    if (!flag) return;
    revertMutation.mutate(
      { name: flag.name },
      {
        onSuccess: (res) => {
          toast.success(
            `Reverted “${res.feature}” to its committed default${
              res.version != null ? ` · v${res.version}` : ''
            }`,
          );
          onClose();
        },
        onError: (err: any) => {
          // 404 override_not_found · 409 db_store_disabled · 403 no permission
          if (err?.status === 404) {
            toast.error('No override is stored for this flag — nothing to revert.');
          } else if (err?.status === 409) {
            toast.error('The DB store is disabled, so overrides cannot be reverted.');
          } else {
            toast.error(
              featureFlagPermissionError(err) ?? err?.message ?? 'Failed to revert the flag.',
            );
          }
        },
      },
    );
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-gray-500" />
            Revert to default?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes the stored override for{' '}
            <span className="font-mono text-xs font-medium text-gray-700">{flag?.name}</span> and
            lets it fall back to its committed ff-api JSON default. The change propagates to all
            workers within one poll interval.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={revertMutation.isPending}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleRevert}
            disabled={revertMutation.isPending}
          >
            {revertMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Revert
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RevertToDefaultDialog;
